/**
 * Checks if a pathname matches a navigation link as a complete path segment.
 * Ensures that the link appears right after the account slug, not nested deeper in the path.
 *
 * @param pathname - The current URL pathname
 * @param link - The navigation link to match against
 * @param slug - The account slug
 * @returns True if the pathname matches the link right after the slug
 *
 * @example
 * matchesPath('/nikosz/ai-agents', '/ai-agents', 'nikosz') // true
 * matchesPath('/nikosz/ai-agents/', '/ai-agents', 'nikosz') // true
 * matchesPath('/nikosz/ai-agents/settings', '/ai-agents', 'nikosz') // true
 * matchesPath('/nikosz/analytics/overview', '/analytics', 'nikosz') // true
 * matchesPath('/nikosz/analytics/ai-agents-effectiveness', '/ai-agents', 'nikosz') // false
 * matchesPath('/nikosz/campaigns/123/analytics', '/analytics', 'nikosz') // false
 */
export function matchesPath(pathname: string, link: string, slug: string): boolean {
  if (!pathname || !link || !slug) {
    return false;
  }

  // Expected pattern: /{slug}{link} followed by nothing, /, or ?
  const expectedStart = `/${slug}${link}`;

  // Check if pathname starts with the expected pattern
  if (!pathname.startsWith(expectedStart)) {
    return false;
  }

  // Check what comes after the expected pattern
  const afterLink = pathname.slice(expectedStart.length);

  // The link matches if it's followed by: nothing, /, or ?
  return afterLink === '' || afterLink[0] === '/' || afterLink[0] === '?';
}
